import {Component, OnInit, inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {Router} from '@angular/router';
import {MatDialog} from '@angular/material/dialog';
import {MatMenuModule} from '@angular/material/menu';
import {MatSnackBar} from '@angular/material/snack-bar';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {Roles} from '@core/enums/Roles';
import {Account, AdminApiService, ClientCompany, apiErrorMessage} from '@services/api/admin/admin-api.service';
import {UserStorageService} from '@services/local-storage/user-storage/user-storage.service';
import {ModalsConfirmComponent} from '@components/modals/modals-confirm/modals-confirm.component';
import {CompanyDialogComponent, CompanyDialogResult} from '../dialogs/company-dialog.component';
import {AccountDialogComponent} from '../dialogs/account-dialog.component';
import {InviteLinkDialogComponent} from '../dialogs/invite-link-dialog.component';

@Component({
  selector: 'app-admin-companies',
  standalone: true,
  imports: [FormsModule, MatMenuModule, MatProgressSpinnerModule, TranslateModule, TablerIconsModule],
  templateUrl: './admin-companies.component.html',
  styleUrl: '../admin-page.scss',
})
export class AdminCompaniesComponent implements OnInit {
  private api = inject(AdminApiService);
  private dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private translate = inject(TranslateService);
  private userStorage = inject(UserStorageService);
  private router = inject(Router);

  companies: ClientCompany[] = [];
  myCompanyId: number | null = null;
  search = '';
  loading = true;
  error = '';

  async ngOnInit(): Promise<void> {
    const me = await this.userStorage.getUserStorage();
    this.myCompanyId = me?.company?.id ?? null;
    this.load();
  }

  get visibleCompanies(): ClientCompany[] {
    const term = this.search.trim().toLowerCase();
    return term
      ? this.companies.filter((c) => `${c.name} ${c.vat_number} ${c.email}`.toLowerCase().includes(term))
      : this.companies;
  }

  load(): void {
    this.loading = true;
    this.error = '';
    this.api.listCompanies().subscribe({
      next: (companies) => {
        this.companies = companies;
        this.loading = false;
      },
      error: (err) => {
        this.loading = false;
        this.error = apiErrorMessage(err, this.translate.instant('admin.error_load'));
      },
    });
  }

  openCreate(): void {
    this.dialog.open(CompanyDialogComponent, {width: '640px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'})
      .afterClosed().subscribe((result?: CompanyDialogResult) => {
        if (!result) {
          this.load();
          return;
        }
        if (result.admin?.invite_link) {
          this.showInvite(result.admin);
        }
        this.load();
      });
  }

  openEdit(company: ClientCompany): void {
    this.dialog.open(CompanyDialogComponent, {data: company, width: '640px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'})
      .afterClosed().subscribe((result?: CompanyDialogResult) => {
        if (result) {
          this.toast(this.translate.instant('admin.company_saved'));
          this.load();
        }
      });
  }

  inviteAdmin(company: ClientCompany): void {
    this.dialog.open(AccountDialogComponent, {
      data: {companies: this.companies, isPlatformAdmin: true, presetCompany: company.id, presetRole: Roles.MANAGEMENT_SYSTEM},
      width: '560px', maxWidth: '94vw', panelClass: 'dm-admin-dialog',
    }).afterClosed().subscribe((account?: Account) => {
      if (account?.invite_link) {
        this.showInvite(account);
        this.load();
      }
    });
  }

  showUsers(company: ClientCompany): void {
    this.router.navigate(['/admin/users'], {queryParams: {company: company.id}});
  }

  setEnabled(company: ClientCompany, enabled: boolean): void {
    const run = () => this.api.updateCompany(company.id, {enabled}).subscribe({
      next: () => {
        this.toast(this.translate.instant(enabled ? 'admin.company_enabled' : 'admin.company_disabled'));
        this.load();
      },
      error: (err) => this.toast(apiErrorMessage(err, this.translate.instant('admin.error_generic'))),
    });
    if (enabled) {
      run();
      return;
    }
    this.dialog.open(ModalsConfirmComponent, {
      panelClass: 'dm-admin-dialog',
      data: {
        title: 'admin.disable_company_title',
        message: this.translate.instant('admin.disable_company_message', {name: company.name}),
        confirmText: 'admin.disable',
        cancelText: 'admin.cancel',
      },
    }).afterClosed().subscribe((confirmed) => confirmed && run());
  }

  private showInvite(account: Account): void {
    this.dialog.open(InviteLinkDialogComponent, {data: account, width: '560px', maxWidth: '94vw', panelClass: 'dm-admin-dialog'});
  }

  private toast(message: string): void {
    this.snackBar.open(message, this.translate.instant('admin.close'), {duration: 4000});
  }
}
